import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Waves, Search, Menu, X } from "lucide-react";

const navLinks = [
  { to: "/software", label: "Reviews" },
  { to: "/best-pool-service-software", label: "Best Of" },
  { to: "/comparisons", label: "Comparisons" },
  { to: "/guides", label: "Guides" },
  { to: "/tools", label: "Tools" },
];

export default function Layout() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
      setQ("");
      setOpen(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center gap-6 h-16">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 shrink-0">
            <span className="w-8 h-8 rounded-full bg-sky-500 flex items-center justify-center">
              <Waves className="w-4 h-4 text-white" aria-hidden="true" />
            </span>
            <span className="font-heading font-800 font-bold text-slate-900 text-sm sm:text-base leading-tight">
              PoolServiceSoftware<span className="text-sky-500">Guide</span>
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-1 flex-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                    isActive ? "bg-sky-50 text-sky-600" : "text-slate-600 hover:text-sky-600 hover:bg-slate-50"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <form onSubmit={submitSearch} className="hidden md:flex items-center ml-auto">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input
                data-testid="nav-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search software, guides..."
                className="pl-9 pr-4 py-2 w-56 rounded-full border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
              />
            </div>
          </form>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden ml-auto md:ml-0 p-2 text-slate-700"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-border bg-white px-4 py-3 space-y-1">
            {navLinks.map((l) => (
              <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} className="block px-3 py-2 rounded-md text-sm font-medium text-slate-700 hover:bg-sky-50">
                {l.label}
              </NavLink>
            ))}
            <form onSubmit={submitSearch} className="pt-2">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search..."
                className="w-full px-4 py-2 rounded-full border border-border text-sm"
              />
            </form>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-slate-900 text-slate-300 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-8 h-8 rounded-full bg-sky-500 flex items-center justify-center">
                <Waves className="w-4 h-4 text-white" aria-hidden="true" />
              </span>
              <span className="font-heading font-bold text-white">PoolServiceSoftwareGuide</span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed max-w-sm">
              Independent reviews, comparisons and tools that help pool service companies choose the right software.
            </p>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-white text-sm mb-3">Explore</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/software" className="hover:text-sky-400">Software Reviews</Link></li>
              <li><Link to="/best-pool-service-software" className="hover:text-sky-400">Best Pool Software</Link></li>
              <li><Link to="/comparisons" className="hover:text-sky-400">Comparisons</Link></li>
              <li><Link to="/guides" className="hover:text-sky-400">Guides</Link></li>
              <li><Link to="/tools" className="hover:text-sky-400">Free Tools</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-white text-sm mb-3">Company</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="hover:text-sky-400">About</Link></li>
              <li><Link to="/methodology" className="hover:text-sky-400">Methodology</Link></li>
              <li><Link to="/contact" className="hover:text-sky-400">Contact</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-800 py-6 px-4 text-center text-xs text-slate-500 space-y-1.5">
          <p>Some links on this site are affiliate links; we may earn a commission at no cost to you. This never affects our rankings — see our <Link to="/methodology" className="underline hover:text-sky-400">methodology</Link>.</p>
          <p>© 2026 PoolServiceSoftwareGuide.com — Independent software research for pool professionals.</p>
        </div>
      </footer>
    </div>
  );
}
